import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { Tenant } from '@src/api/tenant/auth/tenantAuthStatusApiClient';
import { tenantApiClient, TenantUpdateErrorResponse } from '@src/api/tenant/tenant/tenantApiClient';
import { useToast } from '@src/features/toast/context/toastContext';
import { useScrollToError } from '@src/hooks/useScrollToError';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import Button from '@src/ui/elements/button/Button';
import { ErrorMessage } from '@src/ui/elements/form';
import Input from '@src/ui/elements/form/Input';
import Title from '@src/ui/elements/typography/Title.tsx';
import { firstError, hasError } from '@src/utils/errorMessageUtils';
import React, { useCallback, useEffect, useState } from 'react';

const TenantEditForm: React.FC = () => {
  type FormValues = {
    name: string;
    nameKana: string;
    familyName: string;
    firstName: string;
    familyNameKana: string;
    firstNameKana: string;
    phoneNumber: string;
    adminEmail: string;
  };

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [tenantId, setTenantId] = useState<number | null>(null);
  const [errors, setErrors] = useState<TenantUpdateErrorResponse>();
  const toast = useToast();
  const [formValues, setFormValues] = useState<FormValues>();

  const fetchLoginStatus = useCallback(async () => {
    const res = await tenantApiClient.show();
    handleApiResponse(res, (data) => {
      setTenant(data.tenant);
      setTenantId(data.tenant?.id ?? null);
      setFormValues({
        name: data.tenant?.name || '',
        nameKana: data.tenant?.name_kana || '',
        familyName: data.tenant?.family_name || '',
        firstName: data.tenant?.first_name || '',
        familyNameKana: data.tenant?.family_name_kana || '',
        firstNameKana: data.tenant?.first_name_kana || '',
        phoneNumber: data.tenant?.phone_number || '',
        adminEmail: data.tenant?.admin_email || '',
      });
    });
  }, []);

  useEffect(() => {
    fetchLoginStatus();
  }, [fetchLoginStatus]);

  const update = async () => {
    setErrors(undefined);
    if (!formValues) {
      return;
    }

    const response = await tenantApiClient.update(Number(tenantId), {
      tenant: {
        name: formValues.name,
        name_kana: formValues.nameKana,
        family_name: formValues.familyName,
        first_name: formValues.firstName,
        family_name_kana: formValues.familyNameKana,
        first_name_kana: formValues.firstNameKana,
        admin_email: formValues.adminEmail,
        phone_number: formValues.phoneNumber,
      },
    });
    handleApiResponse(
      response,
      () => {
        toast(TOAST_MESSAGE.UPDATE);
        fetchLoginStatus();
        window.scrollTo(0, 0);
      },
      (errors) => {
        setErrors(errors);
      },
    );
  };

  const fieldRefs = useScrollToError(errors ?? {});

  return (
    <main className=''>
      <div>
        <div className='flex flex-col'>
          <div className='flex justify-between gap-4'>
            <Title>{tenant?.name}</Title>
          </div>
        </div>
        {formValues && (
          <div className='mx-auto max-w-[800px] pb-16 pt-20'>
            <form
              className='flex flex-col gap-[26px]'
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>テナントコード</label>
                </div>
                <p>{tenant?.code}</p>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>テナント名</label>
                  <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                    必須
                  </div>
                </div>
                <div className='flex gap-4'>
                  <div
                    className='flex w-full flex-col gap-1'
                    ref={(el) => (fieldRefs.current['tenant.name'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='いろは商事株式会社'
                      value={formValues?.name || ''}
                      onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
                      error={hasError(errors?.tenant.name)}
                    />
                    <ErrorMessage>{firstError(errors?.tenant.name)}</ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>テナント名（フリガナ）</label>
                  <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                    必須
                  </div>
                </div>
                <div className='flex gap-4'>
                  <div
                    className='flex w-full flex-col gap-1'
                    ref={(el) => (fieldRefs.current['tenant.name_kana'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='イロハショウジカブシキガイシャ'
                      value={formValues?.nameKana || ''}
                      onChange={(e) => setFormValues({ ...formValues, nameKana: e.target.value })}
                      error={hasError(errors?.tenant.name_kana)}
                    />
                    <ErrorMessage>{firstError(errors?.tenant.name_kana)}</ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>担当者名</label>
                  <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                    必須
                  </div>
                </div>
                <div className='flex gap-4'>
                  <div
                    className='flex w-full flex-col gap-1'
                    ref={(el) => (fieldRefs.current['tenant.family_name'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='山田'
                      value={formValues?.familyName || ''}
                      onChange={(e) => setFormValues({ ...formValues, familyName: e.target.value })}
                      error={hasError(errors?.tenant.family_name)}
                    />
                    <ErrorMessage>{firstError(errors?.tenant.family_name)}</ErrorMessage>
                  </div>
                  <div
                    className='flex w-full flex-col gap-1'
                    ref={(el) => (fieldRefs.current['tenant.first_name'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='太郎'
                      value={formValues?.firstName || ''}
                      onChange={(e) => setFormValues({ ...formValues, firstName: e.target.value })}
                      error={hasError(errors?.tenant.first_name)}
                    />
                    <ErrorMessage>{firstError(errors?.tenant.first_name)}</ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>担当者名（フリガナ）</label>
                  <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                    必須
                  </div>
                </div>
                <div className='flex gap-4'>
                  <div
                    className='flex w-full flex-col gap-1'
                    ref={(el) => (fieldRefs.current['tenant.family_name_kana'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='ヤマダ'
                      value={formValues?.familyNameKana || ''}
                      onChange={(e) =>
                        setFormValues({ ...formValues, familyNameKana: e.target.value })
                      }
                      error={hasError(errors?.tenant.family_name_kana)}
                    />
                    <ErrorMessage>{firstError(errors?.tenant.family_name_kana)}</ErrorMessage>
                  </div>
                  <div
                    className='flex w-full flex-col gap-1'
                    ref={(el) => (fieldRefs.current['tenant.first_name_kana'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='タロウ'
                      value={formValues?.firstNameKana || ''}
                      onChange={(e) =>
                        setFormValues({ ...formValues, firstNameKana: e.target.value })
                      }
                      error={hasError(errors?.tenant.first_name_kana)}
                    />
                    <ErrorMessage>{firstError(errors?.tenant.first_name_kana)}</ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>担当者メールアドレス</label>
                  <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                    必須
                  </div>
                </div>
                <div className='flex gap-4'>
                  <div
                    className='flex w-full flex-col gap-1'
                    ref={(el) => (fieldRefs.current['tenant.admin_email'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='sample@example.com'
                      value={formValues?.adminEmail || ''}
                      onChange={(e) => setFormValues({ ...formValues, adminEmail: e.target.value })}
                      error={hasError(errors?.tenant.admin_email)}
                    />
                    <ErrorMessage>{firstError(errors?.tenant.admin_email)}</ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>担当者電話番号</label>
                  <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                    必須
                  </div>
                </div>
                <div className='flex gap-4'>
                  <div
                    className='flex w-full flex-col gap-1'
                    ref={(el) => (fieldRefs.current['tenant.phone_number'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='00011112222'
                      value={formValues?.phoneNumber || ''}
                      onChange={(e) =>
                        setFormValues({ ...formValues, phoneNumber: e.target.value })
                      }
                      error={hasError(errors?.tenant.phone_number)}
                    />
                    <ErrorMessage>{firstError(errors?.tenant.phone_number)}</ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>搭載サブサービス</label>
                </div>
                <div className='flex gap-4'>
                  <ul className='list-outside list-disc pl-5'>
                    <li>
                      <span
                        className={`${!tenant?.service.enterprise_portal ? 'text-gray-300' : ''}`}
                      >
                        ポータル
                      </span>
                    </li>
                    <li>
                      <span className={`${!tenant?.service.dx_diagnosis ? 'text-gray-300' : ''}`}>
                        DX診断
                      </span>
                    </li>
                    <li>
                      <span
                        className={`${!tenant?.service.online_community ? 'text-gray-300' : ''}`}
                      >
                        オンラインコミュニティ
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className='flex justify-center gap-5 pt-4'>
                <div className='w-[172px]'>
                  <Button
                    fullRounded
                    fullWidth
                    outline
                    onClick={update}
                  >
                    保存する
                  </Button>
                </div>
              </div>
            </form>
          </div>
        )}
      </div>
    </main>
  );
};

export default TenantEditForm;
